import {objectFromEntries} from '@benchling/js-utils';

import {ActionOrigin, AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {TransferStructuredTable} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {camelCaseToSnake} from 'util/NameUtil';

import {AnalyticsEventMetadata, NormalizedAnalyticsEventMetadata} from './AnalyticsTypes';

export function structuredTableActionOrigin(structuredTable: UnknownStructuredTable): ActionOrigin {
  switch (structuredTable.kind) {
    case StructuredTableKind.MIXTURE_METADATA:
    case StructuredTableKind.MIXTURE_INGREDIENTS:
      return ActionOrigin.NOTEBOOK_ENTRY_MIXTURE_PREP_TABLE;
    case StructuredTableKind.INVENTORY:
      return ActionOrigin.NOTEBOOK_ENTRY_INVENTORY_UPDATE_TABLE;
    case StructuredTableKind.PLATE_CREATION:
      return ActionOrigin.NOTEBOOK_ENTRY_PLATE_CREATION_TABLE;
    case StructuredTableKind.BOX_CREATION:
      return ActionOrigin.NOTEBOOK_ENTRY_BOX_CREATION_TABLE;
    case StructuredTableKind.RUN_OUTPUT_SAMPLES:
      return ActionOrigin.NOTEBOOK_ENTRY_RUN_OUTPUT_SAMPLES_TABLE;
    case StructuredTableKind.REGISTRATION:
      return ActionOrigin.NOTEBOOK_ENTRY_REGISTRATION_TABLE;
    case StructuredTableKind.RESULTS:
      return ActionOrigin.NOTEBOOK_ENTRY_RESULTS_TABLE;
    case StructuredTableKind.TRANSFER:
      switch ((structuredTable as TransferStructuredTable).metadata.mode) {
        case InventoryStructuredTableMode.CREATE:
          return ActionOrigin.NOTEBOOK_ENTRY_CONTAINER_CREATION_TABLE;
        case InventoryStructuredTableMode.FILL:
          return ActionOrigin.NOTEBOOK_ENTRY_FILL_EXISTING_TABLE;
        default:
          return ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE;
      }
    case StructuredTableKind.PROCESS_OUTPUT:
      switch ((structuredTable as ProcessOutputStructuredTable).metadata.tableLocation) {
        case ProcessTableLocation.WORKSPACE_TAB:
        case ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB:
          return ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_OUTPUT_TABLE;
        case ProcessTableLocation.ENTRY:
          return ActionOrigin.NOTEBOOK_ENTRY_WORKFLOW_OUTPUT_TABLE;
        case ProcessTableLocation.DIRECT_EXECUTION_MODAL:
          return ActionOrigin.WORKFLOWS_DIRECT_EXECUTION_MODAL_WORKFLOW_OUTPUT_TABLE;
        case ProcessTableLocation.CREATE_MODAL:
        case ProcessTableLocation.STATUS_CONFIRMATION_MODAL:
        case ProcessTableLocation.FIELD_MAPPING_MODAL:
        default:
          return ActionOrigin.WORKFLOWS_UNDETERMINED_STRUCTURED_TABLE;
      }
    case StructuredTableKind.PROCESS_TASK:
      switch ((structuredTable as ProcessTaskStructuredTable).metadata.tableLocation) {
        case ProcessTableLocation.WORKSPACE_TAB:
        case ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB:
          return ActionOrigin.WORKFLOWS_TASK_GROUP_WORKFLOW_TASK_TABLE;
        case ProcessTableLocation.ENTRY:
        case ProcessTableLocation.REQUEST_V2_DOCUMENT:
          return ActionOrigin.NOTEBOOK_ENTRY_WORKFLOW_TASK_TABLE;
        case ProcessTableLocation.DIRECT_EXECUTION_MODAL:
          return ActionOrigin.WORKFLOWS_DIRECT_EXECUTION_MODAL_WORKFLOW_TASK_TABLE;
        case ProcessTableLocation.CREATE_MODAL:
        case ProcessTableLocation.STATUS_CONFIRMATION_MODAL:
        case ProcessTableLocation.FIELD_MAPPING_MODAL:
        default:
          return ActionOrigin.WORKFLOWS_UNDETERMINED_STRUCTURED_TABLE;
      }
    case StructuredTableKind.LOOKUP:
      return ActionOrigin.NOTEBOOK_ENTRY_LOOKUP_TABLE;
    default:
      return ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE;
  }
}

function standardizeRecursively(
  metadata: AnalyticsMetadataForContext | AnalyticsEventMetadata
): AnalyticsEventMetadata {
  if (Array.isArray(metadata)) {
    return metadata.map((i) => (typeof i === 'object' && i != null ? standardizeRecursively(i) : i));
  }
  if (typeof metadata === 'object' && metadata != null) {
    return objectFromEntries(
      Object.entries(metadata).map(([key, value]) => [camelCaseToSnake(key), standardizeRecursively(value)])
    );
  }
  return metadata;
}

export function standardAnalyticsMetadataToDict(
  analyticsMetadata: AnalyticsEventMetadata,
  defaultAnalyticsMetadata?: AnalyticsEventMetadata
): NormalizedAnalyticsEventMetadata {
  const metadataWithDefaults = {...defaultAnalyticsMetadata, ...analyticsMetadata};
  return standardizeRecursively(metadataWithDefaults) as Object;
}
